import { Typography } from "@mui/material"
import type { FC } from "react"
import { useMemo } from "react"
import { useFormContext } from "react-hook-form"

import { STOCK_TYPE_OPTIONS } from "../../constants"
import type { ProductWarehouseFormValues } from "../schemas"

import { FormBooleanRadioGroup, FormCheckbox, FormNumericField, FormSelect } from "@/components/UiKit"
import { LeadTimeUnits, ProductStockType, type ProductWarehouseArea } from "@/graphql/codegen/graphql"
import { useLookupWarehouses } from "@/hooks/useLookupWarehouses"
import { formatLeadTimeUnit } from "@/screens/Products/utils"
import type { Option } from "@/types/form-fields"

const LEAD_TIME_UNIT_OPTIONS: Option<LeadTimeUnits>[] = Object.values(LeadTimeUnits).map((unit) => ({
  value: unit,
  label: formatLeadTimeUnit(unit),
}))

interface ProductWarehouseFormProps {
  isEdit: boolean
  existingWarehouseAreas: ProductWarehouseArea[]
}

export const ProductWarehouseForm: FC<ProductWarehouseFormProps> = ({ isEdit, existingWarehouseAreas }) => {
  const { control, watch, setValue } = useFormContext<ProductWarehouseFormValues>()
  const { warehouses } = useLookupWarehouses()
  const isActive = watch("isActive")

  const warehouseOptions = useMemo<Option<string>[]>(() => {
    const options = warehouses.map(({ id, label }) => ({ value: id, label }))

    if (isEdit) {
      return options
    }

    const existingWarehouseAreaIds = new Set(existingWarehouseAreas.map(({ warehouseAreaId }) => warehouseAreaId))

    return options.filter(({ value }) => !existingWarehouseAreaIds.has(value))
  }, [warehouses, isEdit, existingWarehouseAreas])

  const handleIsActiveChange = (value: boolean) => {
    setValue("stockType", value ? null : ProductStockType.NonStockItem, { shouldDirty: true, shouldValidate: true })
  }

  return (
    <div className='flex flex-col gap-6'>
      <FormSelect
        control={control}
        name='warehouseAreaId'
        label='Warehouse'
        required
        placeholder='Select one'
        options={warehouseOptions}
        disabled={isEdit}
        data-testid='product.warehouse-form.warehouse-field'
      />
      <FormBooleanRadioGroup
        control={control}
        name='isActive'
        label='Warehouse Association Status'
        variant='active-inactive'
        disabled={!isEdit}
        onChange={handleIsActiveChange}
        data-testid='product.warehouse-form.is-active-field'
      />
      <FormNumericField
        control={control}
        name='safetyStock'
        label='Akrochem Safety Stock (US UoM)'
        decimalScale={0}
        allowNegative={false}
        disabled={!isActive}
        data-testid='product.warehouse-form.safety-stock-field'
      />
      <FormSelect
        control={control}
        name='stockType'
        label='Akrochem Stock Status'
        placeholder='Select one'
        options={STOCK_TYPE_OPTIONS}
        disabled={!isActive}
        data-testid='product.warehouse-form.stock-type-field'
      />
      <div>
        <Typography className='mb-2 text-xs text-gray-800'>
          Select other relevant conditions for this product
        </Typography>
        <div className='flex flex-col gap-1'>
          <FormCheckbox
            control={control}
            name='hasCombinedPricing'
            label='Combined Pricing'
            disabled={!isActive}
            data-testid='product.warehouse-form.combined-pricing-field'
          />
          <FormCheckbox
            control={control}
            name='isOnAllocation'
            label='Allocation'
            disabled={!isActive}
            data-testid='product.warehouse-form.allocation-field'
          />
        </div>
      </div>
      <FormNumericField
        control={control}
        name='weightPerTruckload'
        label='US UoM per Truckload'
        decimalScale={0}
        allowNegative={false}
        disabled={!isActive}
        data-testid='product.warehouse-form.weight-per-truckload-field'
      />
      <div className='flex gap-x-4'>
        <FormNumericField
          control={control}
          name='leadTime'
          label='Vendor Lead Time'
          decimalScale={0}
          allowNegative={false}
          disabled={!isActive}
          data-testid='product.warehouse-form.lead-time-field'
        />
        <FormSelect
          control={control}
          className='mt-[22px] w-32'
          name='leadTimeUnit'
          aria-label='Vendor Lead Time Unit'
          options={LEAD_TIME_UNIT_OPTIONS}
          disabled={!isActive}
          data-testid='product.warehouse-form.lead-time-unit-field'
        />
      </div>
      <FormNumericField
        control={control}
        name='minOrderQuantity'
        label='Vendor Min Order Qty (US UoM)'
        decimalScale={0}
        allowNegative={false}
        disabled={!isActive}
        data-testid='product.warehouse-form.min-order-qty-field'
      />
    </div>
  )
}
